import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';

const ComparisonContext = createContext();

const MAX_COMPARE = 5;
const STORAGE_KEY = 'compareList';

function loadCompareList() {
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch {
        return [];
    }
}

export const ComparisonProvider = ({ children }) => {
    const [compareList, setCompareList] = useState(loadCompareList);

    // Sync to sessionStorage on every change
    useEffect(() => {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(compareList));
    }, [compareList]);

    const addToCompare = useCallback((property) => {
        setCompareList(prev => {
            if (prev.length >= MAX_COMPARE) return prev;
            if (prev.some(p => p.property_id === property.property_id)) return prev;
            return [...prev, {
                property_id: property.property_id,
                address: property.address,
                city: property.city,
                state: property.state,
                list_price: property.list_price,
                beds: property.beds,
                baths: property.baths,
                sqft: property.sqft,
                primary_photo_url: property.primary_photo_url,
                arv: property.arv,
                estimated_rent: property.estimated_rent,
                roi_percentage: property.roi_percentage
            }];
        });
    }, []);

    const removeFromCompare = useCallback((propertyId) => {
        setCompareList(prev => prev.filter(p => p.property_id !== propertyId));
    }, []);

    const clearCompare = useCallback(() => {
        setCompareList([]);
    }, []);

    const isInCompare = useCallback((propertyId) => {
        return compareList.some(p => p.property_id === propertyId);
    }, [compareList]);

    const setCompareListFromSaved = useCallback((properties) => {
        setCompareList(properties.slice(0, MAX_COMPARE));
    }, []);

    const value = useMemo(() => ({
        compareList,
        compareCount: compareList.length,
        addToCompare,
        removeFromCompare,
        clearCompare,
        isInCompare,
        setCompareListFromSaved
    }), [compareList, addToCompare, removeFromCompare, clearCompare, isInCompare, setCompareListFromSaved]);

    return (
        <ComparisonContext.Provider value={value}>
            {children}
        </ComparisonContext.Provider>
    );
};

export const useComparison = () => {
    const context = useContext(ComparisonContext);
    if (!context) {
        throw new Error('useComparison must be used within a ComparisonProvider');
    }
    return context;
};
